// BewertungenScreen — Patientenbewertungen (Umfrage, Google, Portale):
// Liste mit Antworten-Funktion, Plattform-Karten, interne Umfrage.
// Lebt seit dem Feedback-Merge im Tracking (Tab "Feedback" > "Bewertungen").
import { useState, useEffect } from "react";
import { Met, Sp, Btn, Select, Textarea, Z, toast } from "./ui";
import { reviewStore } from "./lib/docStore";
import { demoFallbacks, serverActive } from "./lib/mode";
import UmfrageCard, { UmfrageNachTerminCard } from "./UmfrageCard";
import { supabase } from "./lib/supabase";
import { getSetting, saveSetting } from "./lib/settingsStore";

// ── Google-Bewertungen live (Places API über Edge Function) ────────────────
function GoogleLiveCard() {
  const [placeId, setPlaceId] = useState(() => getSetting("google_place_id") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (pid) => {
    if (!serverActive() || !pid) return;
    setBusy(true); setErr("");
    try {
      const { data: d, error } = await supabase.functions.invoke("google-reviews", { body: { placeId: pid } });
      if (error) { let m = ""; try { const b = await error.context?.json?.(); m = b?.error || ""; } catch {} throw new Error(m || error.message); }
      if (d?.error) throw new Error(d.error);
      setData(d);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };
  useEffect(() => { const pid = getSetting("google_place_id"); if (pid) load(pid); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const pid = placeId.trim();
    try { await saveSetting("google_place_id", pid); toast("Place ID gespeichert"); if (pid) load(pid); }
    catch { toast("Speichern fehlgeschlagen — nur der Inhaber darf das.", "r"); }
  };

  if (!serverActive()) return null;
  return (
    <div style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderRadius:12,padding:14}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <div style={{width:24,height:24,borderRadius:6,background:"#4285F4",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>G</div>
        <span style={{fontSize:12,fontWeight:600}}>Google-Bewertungen (live)</span>
        {data&&<span style={{fontSize:10,color:"var(--mf-muted)"}}>{data.name}</span>}
        <div style={{flex:1}}/>
        {data&&<Btn small onClick={()=>load(placeId)}>{busy?"Lade…":"Aktualisieren"}</Btn>}
      </div>
      {!getSetting("google_place_id") && !data ? (
        <div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.6}}>
          Verbinde das Google-Unternehmensprofil der Praxis: Place ID über den
          {" "}<a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" style={{color:"var(--mf-primary)"}}>Place-ID-Finder</a>{" "}
          suchen und hier eintragen. (Voraussetzung: Edge Function <strong>google-reviews</strong> deployt + Secret <strong>GOOGLE_PLACES_API_KEY</strong>.)
          <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
            <input value={placeId} onChange={e=>setPlaceId(e.target.value)} placeholder="Place ID, z.B. ChIJ…"
              style={{flex:1,minWidth:200,fontSize:11,padding:"7px 10px",borderRadius:10,border:"1px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit"}}/>
            <Btn color="g" onClick={save}>Verbinden</Btn>
          </div>
        </div>
      ) : err ? (
        <div style={{fontSize:11,color:"var(--mf-neg)"}}>{err}</div>
      ) : !data ? (
        <div style={{fontSize:11,color:"var(--mf-muted)"}}>{busy?"Lade Google-Daten…":"Noch keine Daten geladen."}</div>
      ) : (
        <>
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:8}}>
            <span style={{fontSize:24,fontWeight:600}}>{data.rating!=null?String(data.rating).replace(".",","):"—"}</span>
            <span style={{color:"#BA7517",fontSize:13,letterSpacing:1}}>{"★".repeat(Math.round(data.rating||0))}</span>
            <span style={{fontSize:11,color:"var(--mf-muted)"}}>{data.count} Bewertungen</span>
            {data.mapsUri&&<a href={data.mapsUri} target="_blank" rel="noreferrer" style={{fontSize:10,color:"var(--mf-primary)",marginLeft:"auto"}}>Auf Google ansehen →</a>}
          </div>
          {(data.reviews||[]).slice(0,4).map((rv,i)=>(
            <div key={i} style={{padding:"7px 0",borderTop:"0.5px solid var(--mf-border)"}}>
              <div style={{display:"flex",gap:6,alignItems:"center",fontSize:10.5}}>
                <strong>{rv.author}</strong>
                <span style={{color:"#BA7517"}}>{"★".repeat(rv.stars)}</span>
                <span style={{color:"var(--mf-muted)"}}>{rv.date}</span>
              </div>
              {rv.text&&<div style={{fontSize:11,color:"var(--mf-text)",marginTop:2,lineHeight:1.5}}>{rv.text.length>220?rv.text.slice(0,220)+"…":rv.text}</div>}
            </div>
          ))}
          <div style={{fontSize:9.5,color:"var(--mf-muted)",marginTop:6}}>Antworten auf Google-Rezensionen direkt im Google-Unternehmensprofil — dida zeigt hier den Live-Stand.</div>
        </>
      )}
    </div>
  );
}

function ReviewPanel({ r, srcMeta, Stars, onClose, onSave }) {
  const [reply, setReply] = useState(r.antwort || "");
  const m = srcMeta[r.src];
  return (
    <>
      <div style={{padding:"12px 14px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:8,position:"sticky",top:0,background:"var(--mf-bg)"}}>
        <div style={{width:34,height:34,borderRadius:"50%",background:m.bg,color:m.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:500}}>{r.name.split(" ").map(w=>w[0]).join("")}</div>
        <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500}}>{r.name}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{r.date} · {m.label}</div></div>
        <div onClick={onClose} style={{cursor:"pointer",fontSize:18,color:"var(--mf-muted)"}}>×</div>
      </div>
      <div style={{padding:"12px 14px",flex:1,overflowY:"auto"}}>
        <Stars n={r.stars} size={16}/>
        <div style={{fontSize:12,color:"var(--mf-text)",lineHeight:1.6,margin:"8px 0"}}>{r.text}</div>
        <div style={{height:"0.5px",background:"var(--mf-border)",margin:"7px 0"}}/>
        <Textarea label="Antwort verfassen" value={reply} onChange={setReply} rows={4} style={{marginTop:8}}/>
        <div style={{display:"flex",gap:5,marginTop:6}}>
          <Btn color="g" onClick={()=>{onSave(r.id,reply);onClose();}}>Antwort senden</Btn>
          <Btn onClick={onClose}>Schließen</Btn>
        </div>
      </div>
    </>
  );
}


// ─── NEWSLETTER SCREEN ────────────────────────────────────────────────────────

function BewertungenScreen() {
  const [src, setSrc] = useState("alle");
  const [selR, setSelR] = useState(null);
  const [umfrageOpen, setUmfrageOpen] = useState(false);
  const [umfrageSent, setUmfrageSent] = useState(false);
  const [umfrageTyp, setUmfrageTyp] = useState("zufriedenheit");
  const [umfrageZielgruppe, setUmfrageZielgruppe] = useState("alle");

  const INITIAL_REVIEWS = [
    { id:1, src:"google", name:"Maria M.", date:"heute, 08:31", stars:5, text:"Sehr freundliches Team, der Online-Termin hat super geklappt. Ich wurde sofort behandelt!", antwort:"" },
    { id:2, src:"jameda", name:"Klaus W.", date:"heute, 06:14", stars:4, text:"Kompetente Zahnärztin. Die Extraktion wurde schmerzfrei erklärt, aber die Wartezeit war etwas lang.", antwort:"Vielen Dank für Ihr Feedback, Herr Weber!" },
    { id:3, src:"intern", name:"Anna F.", date:"gestern, 15:22", stars:5, text:"Tolle Praxis! Die Brücke sitzt perfekt und das Team hat sich wirklich Zeit genommen.", antwort:"" },
    { id:4, src:"google", name:"Max B.", date:"gestern, 11:10", stars:3, text:"Füllung war in Ordnung, aber die Wartezeit trotz Termin könnte besser sein.", antwort:"" },
    { id:5, src:"jameda", name:"Eva R.", date:"gestern, 09:05", stars:5, text:"Dr. Hoffmann erklärt jeden Schritt der Parodontitis-Behandlung geduldig. Sehr empfehlenswert!", antwort:"" },
    { id:6, src:"intern", name:"Hans S.", date:"Mo., 14:30", stars:4, text:"Kinderzahnbehandlung ohne Tränen — das Team hat meinen Sohn toll abgelenkt. Danke!", antwort:"Danke für das schöne Feedback!" },
    { id:7, src:"google", name:"Lena B.", date:"Mo., 10:15", stars:5, text:"Das KI-Telefon hat meinen Recall-Termin in 2 Minuten vergeben. Moderne Praxis!", antwort:"" },
    { id:8, src:"jameda", name:"Inge W.", date:"So., 16:44", stars:2, text:"Lange Wartezeiten vor der Prophylaxe, telefonisch schwer erreichbar.", antwort:"" },
  ];
  const [reviews, setReviews] = useState(()=>{ const cached=reviewStore.loadCache(); return cached!==null?cached:(demoFallbacks()?INITIAL_REVIEWS:[]); });
  useEffect(()=>{ let on=true; reviewStore.list().then(r=>{ if(on&&(r.length||!demoFallbacks())) setReviews(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  useEffect(()=>{ if(serverActive()) return; try{localStorage.setItem("dida_reviews",JSON.stringify(reviews));}catch{} },[reviews]); // LIVE spiegelt der Store

  const updateReview = (id, antwort) => {
    setReviews(prev=>prev.map(r=>r.id===id?{...r,antwort}:r)); // optimistisch
    if(serverActive()){ const hit=reviews.find(r=>r.id===id); if(hit) reviewStore.upsert({...hit,antwort}).catch(()=>toast("Antwort nicht gespeichert — Verbindung prüfen.","r")); }
  };

  const filtered = reviews.filter(r=>src==="alle"||r.src===src).sort((a,b)=>(b.id||0)-(a.id||0)); // neueste zuerst
  const SRC_META = { google:{label:"Google",bg:"#E6F1FB",col:"#0C447C"}, jameda:{label:"Jameda",bg:"#E1F5EE",col:"#085041"}, intern:{label:"Intern",bg:"#EEEDFE",col:"#3C3489"} };
  const Stars = ({n,size=12}) => <span style={{color:"#BA7517",fontSize:size,letterSpacing:1}}>{"★".repeat(n)}{"☆".repeat(5-n)}</span>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,position:"relative"}}>
      {/* Top metrics */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        {(()=>{
          const n=reviews.length;
          const avg=n?(reviews.reduce((s,r)=>s+(r.stars||0),0)/n):null;
          const five=n?Math.round(reviews.filter(r=>r.stars===5).length/n*100):null;
          const npsVals=reviews.map(r=>r.nps).filter(v=>typeof v==="number");
          const avgNps=npsVals.length?(npsVals.reduce((s,v)=>s+v,0)/npsVals.length):null;
          return (<>
            <Met v={avg!=null?avg.toFixed(1).replace(".",","):"—"} l="Ø Gesamtbewertung" d={n?`aus ${n} Bewertungen`:"noch keine Bewertungen"} dc={avg>=4?"up":undefined}/>
            <Met v={String(n)} l="Bewertungen gesamt"/>
            <Met v={five!=null?`${five}%`:"—"} l="5-Sterne-Anteil" dc={five>=50?"up":undefined}/>
            <Met v={avgNps!=null?avgNps.toFixed(1).replace(".",","):"—"} l="Ø Weiterempfehlung" d={npsVals.length?"Skala 0–10":"noch keine Angabe"} dc={avgNps>=8?"up":undefined}/>
          </>);
        })()}
      </div>

      {/* Platform scores */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:9}}>
        {[["google","Google","#4285F4"],["jameda","Jameda","#0F6E56"],["intern","Hausinternes System","#3C3489"]].map(([k,label,color])=>{
          const plat=reviews.filter(r=>r.src===k);
          const score=plat.length?(plat.reduce((s,r)=>s+(r.stars||0),0)/plat.length).toFixed(1).replace(".",","):"—";
          const cnt=plat.length;
          return (
          <div key={k} onClick={()=>setSrc(src===k?"alle":k)} style={{background:src===k?"var(--mf-bg-alt)":"var(--mf-bg)",border:`0.5px solid ${src===k?"#0F6E56":"#e7e5e4"}`,borderRadius:"10px",padding:12,cursor:"pointer",transition:"all .12s"}}>
            <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
              <div style={{width:28,height:28,borderRadius:6,background:color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff"}}>{label[0]}</div>
              <div><div style={{fontSize:12,fontWeight:500}}>{label}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{cnt} Bewertungen</div></div>
            </div>
            <div style={{fontSize:22,fontWeight:500}}>{score}</div>
            <Stars n={cnt?Math.round(parseFloat(String(score).replace(",","."))):0} size={11}/>
            {/* Star distribution */}
            <div style={{marginTop:7,display:"flex",flexDirection:"column",gap:2}}>
              {[5,4,3,2,1].map(s=>{
                const plat=reviews.filter(r=>r.src===k);
                const pct=plat.length?Math.round(plat.filter(r=>r.stars===s).length/plat.length*100):0;
                return (
                  <div key={s} style={{display:"flex",alignItems:"center",gap:5,fontSize:9}}>
                    <span style={{color:"var(--mf-muted)",minWidth:12}}>{s}★</span>
                    <div style={{flex:1,height:4,background:"#e7e5e4",borderRadius:2,overflow:"hidden"}}><div style={{height:4,width:`${pct}%`,background:color,borderRadius:2}}/></div>
                    <span style={{color:"var(--mf-muted)",minWidth:20,textAlign:"right"}}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      <GoogleLiveCard/>

      <UmfrageCard/>

      <UmfrageNachTerminCard/>

      {/* Filter row */}
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <div style={{display:"flex",gap:4}}>
          {[["alle","Alle"],["google","Google"],["jameda","Jameda"],["intern","Intern"]].map(([k,l])=>(
            <button key={k} onClick={()=>setSrc(k)} style={{fontSize:10,padding:"3px 10px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:src===k?"#0F6E56":"var(--mf-bg-alt)",color:src===k?"#E1F5EE":"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
        <span style={{fontSize:10,color:"var(--mf-muted)",marginLeft:"auto"}}>{filtered.length} Bewertungen</span>
        <Btn color="g" onClick={()=>{setUmfrageSent(false);setUmfrageOpen(true);}}>+ Interne Umfrage senden</Btn>
      </div>

      {/* Review list */}
      <div style={{display:"flex",flexDirection:"column",gap:7}}>
        {filtered.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",padding:"14px 2px",textAlign:"center"}}>Noch keine Bewertungen in dieser Ansicht.</div>}
        {filtered.map(r=>{
          const anon=r.src==="intern"; // interne Umfrage: anonym, keine Antwort möglich
          const meta=SRC_META[r.src];
          const sp=meta.col==="#085041"?"g":meta.col==="#0C447C"?"b":"pu";
          return (
          <div key={r.id} onClick={anon?undefined:()=>setSelR(r)} style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderLeft:`3px solid ${meta.col}`,borderRadius:"8px",padding:"11px 13px",cursor:anon?"default":"pointer"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:6}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:meta.bg,color:meta.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:500,flexShrink:0}}>
                {anon?<i className="ti ti-message-heart" style={{fontSize:15}}/>:r.name.split(" ").map(w=>w[0]).join("")}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:500}}>{anon?"Anonyme Umfrage":r.name}</div>
                <div style={{fontSize:10,color:"var(--mf-muted)"}}>{r.date}{r.behandler?` · ${r.behandler}`:""}</div>
              </div>
              <Stars n={r.stars}/>
              <Sp color={sp}>{meta.label}</Sp>
              {!anon&&!r.antwort&&<Sp color="a">Ohne Antwort</Sp>}
              {!anon&&r.antwort&&<Sp color="g">Beantwortet</Sp>}
            </div>
            {typeof r.nps==="number"&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,fontWeight:600,padding:"2px 9px",borderRadius:20,marginBottom:7,
                background:r.nps>=9?"#E1F5EE":r.nps>=7?"var(--mf-bg-alt)":"#FBE9E6",
                color:r.nps>=9?"#085041":r.nps>=7?"var(--mf-muted)":"#B3341F"}}>
                Weiterempfehlung {r.nps}/10
              </div>
            )}
            {r.text
              ? <div style={{fontSize:11.5,color:"var(--mf-text)",lineHeight:1.55}}>{r.text}</div>
              : <div style={{fontSize:11,color:"var(--mf-muted)",fontStyle:"italic"}}>Kein Kommentar hinterlassen.</div>}
            {!anon&&r.antwort&&<div style={{fontSize:10,color:"var(--mf-muted)",marginTop:6,padding:"5px 8px",background:"var(--mf-bg-alt)",borderRadius:5,borderLeft:"2px solid #0F6E56"}}>Antwort: {r.antwort}</div>}
          </div>
          );
        })}
      </div>

      {/* Detail / Reply modal */}
      {selR&&<>
        <div onClick={()=>setSelR(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay2}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:420,maxHeight:"85vh",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal2,boxShadow:"0 24px 64px rgba(0,0,0,.3)",overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <ReviewPanel r={reviews.find(r=>r.id===selR.id)||selR} srcMeta={SRC_META} Stars={Stars} onClose={()=>setSelR(null)} onSave={updateReview}/>
        </div>
      </>}

      {/* UMFRAGE MODAL */}
      {umfrageOpen&&<>
        <div onClick={()=>setUmfrageOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay2}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:400,background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal2,boxShadow:"0 24px 64px rgba(0,0,0,.3)",overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:13,fontWeight:500}}>Interne Umfrage senden</div>
            <div onClick={()=>setUmfrageOpen(false)} style={{cursor:"pointer",fontSize:18,color:"var(--mf-muted)"}}>×</div>
          </div>
          {umfrageSent?(
            <div style={{padding:"28px 18px",textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:8}}>📊</div>
              <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Umfrage gesendet!</div>
              <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:16}}>Die Umfrage wurde an alle aktiven Patienten versandt. Ergebnisse erscheinen hier sobald Antworten eingehen.</div>
              <Btn color="g" onClick={()=>setUmfrageOpen(false)}>Schließen</Btn>
            </div>
          ):(
            <div style={{padding:"16px 18px",display:"flex",flexDirection:"column",gap:4}}>
              <Select label="Umfrage-Typ" value={umfrageTyp} onChange={setUmfrageTyp} options={[{value:"zufriedenheit",label:"Allgemeine Zufriedenheit"},{value:"wartezeit",label:"Wartezeit & Erreichbarkeit"},{value:"behandlung",label:"Behandlungsqualität"},{value:"nps",label:"Net Promoter Score (NPS)"}]}/>
              <Select label="Zielgruppe" value={umfrageZielgruppe} onChange={setUmfrageZielgruppe} options={[{value:"alle",label:"Alle Patienten (1.247)"},{value:"recent",label:"Letzte 30 Tage (312)"},{value:"prophylaxe",label:"Prophylaxe-Patienten (848)"}]}/>
              <div style={{background:"var(--mf-bg-alt)",borderRadius:6,padding:"8px 10px",fontSize:10,color:"var(--mf-muted)",marginBottom:8}}>Die Umfrage wird per E-Mail versendet. Patienten ohne E-Mail werden übersprungen.</div>
              <div style={{display:"flex",gap:10}}>
                <Btn color="g" lg style={{flex:1}} onClick={()=>setUmfrageSent(true)}>Senden</Btn>
                <Btn lg onClick={()=>setUmfrageOpen(false)}>Abbrechen</Btn>
              </div>
            </div>
          )}
        </div>
      </>}
    </div>
  );
}

export default BewertungenScreen;
